import {createStyles, Group, Stack, Text} from '@mantine/core';
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {ReactionHighlightQueryInput} from 'chemistry/ReactionChemSearchUtils';
import HighlightedReactionSchemeThumbnail from 'chemistry/reactions/HighlightedReactionSchemeThumbnail';
import ReactionSchemeThumbnail from 'chemistry/reactions/ReactionSchemeThumbnail';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  getStudiesRowContentHeight,
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {decimalRoundingStrategyToDecimalJSRoundingMode} from 'util/Decimals';
import PreciseDecimal, {DecimalNotation} from 'util/precise-decimal/PreciseDecimal';
import {BasicEvent} from 'util/Types';

import {ReactionGraphQL} from '../../graphql-fragments/chemistry/__generated/ReactionGraphQL';
import * as ReactionContextMenu from './ReactionContextMenu';

export interface ReactionListingExtras extends StudyableItemListingExtras {
  /** Substructure queries to highlight, from the reaction search modal's clauses. Empty/absent = no highlighting. */
  highlightQueries?: Array<ReactionHighlightQueryInput>;
  /** Whether the "Highlight substructure in search results" checkbox is checked. */
  shouldHighlightSubstructure?: boolean;
}
export type ReactionColumnExtras = ReactionListingExtras;

const REACTION_SCHEME_HEIGHT = 160;
const PRODUCT_NAME_MAX_WIDTH = 200;

// Match the stoichiometry table's Yield (%) column display precision
// (DEFAULT_EQUIV_PRECISION in StoichiometryStructuredTableAdapter).
const YIELD_DISPLAY_PRECISION = 2;

const useProductYieldStyles = createStyles(() => ({
  productName: {
    maxWidth: PRODUCT_NAME_MAX_WIDTH,
  },
  yieldValue: {
    flexShrink: 0,
  },
}));

function formatYield(value: string): string {
  const preciseDecimal = PreciseDecimal.ofOrUndefined(value);
  if (preciseDecimal == null) {
    return '';
  }
  // For scientific-notation values, show the raw value rather than rounding it
  if (preciseDecimal.notation === DecimalNotation.SCIENTIFIC) {
    return `${preciseDecimal.value}%`;
  }
  const formattedValue = preciseDecimal.decimal
    .toDecimalPlaces(
      YIELD_DISPLAY_PRECISION,
      decimalRoundingStrategyToDecimalJSRoundingMode(BenchlingConfig.SCHEMAS_DECIMAL_ROUNDING_STRATEGY)
    )
    .toString();
  return `${formattedValue}%`;
}

function ProductYield({
  name,
  amountYield,
}: {
  name: string | null | undefined;
  amountYield: string;
}): JSX.Element {
  const {classes} = useProductYieldStyles();
  const formattedYield = formatYield(amountYield);
  if (!name) {
    return <Text size="sm">{formattedYield}</Text>;
  }
  return (
    <Group spacing={0} noWrap={true}>
      <Text size="sm" truncate={true} title={name} className={classes.productName}>
        {name}
      </Text>
      <Text size="sm" className={classes.yieldValue}>
        {`: ${formattedYield}`}
      </Text>
    </Group>
  );
}

export default class ReactionListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.REACTION> {
  listingRendererType = ListingRendererType.REACTION;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={FolderItemIcon.Reaction} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ReactionGraphQL>,
    extras: ReactionListingExtras
  ): Array<ColumnConfiguration<ReactionGraphQL>> {
    const maybeStudiesColumn = maybeGetStudiesListingColumn<ReactionGraphQL>(extras.itemStudyAttributes);

    return _.compact([
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: 'ID',
          flexGrow: 0,
          isSortable: false,
          width: 120,
        },
        render: (item) => item.id,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          width: 200,
        },
        render: (item) => (
          <span key="name" className="sidepanel-primaryLabel">
            {item.name}
          </span>
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.REACTION_SCHEME,
          header: 'Reaction scheme',
          flexGrow: 1,
          isSortable: false,
          width: 400,
          fillCell: true,
        },
        render: (item) => {
          if (!item.scheme) {
            return null;
          }
          const shouldHighlight =
            extras.shouldHighlightSubstructure && (extras.highlightQueries?.length ?? 0) > 0;
          if (shouldHighlight) {
            return (
              <HighlightedReactionSchemeThumbnail
                key={item.id}
                reactionId={item.id}
                reactionName={item.name}
                queries={extras.highlightQueries!}
                fallbackSrc={item.scheme}
              />
            );
          }
          return (
            <ReactionSchemeThumbnail
              key={item.scheme}
              src={item.scheme}
              alt={`Reaction scheme for ${item.name}`}
            />
          );
        },
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.YIELD,
          header: getColumnHeaderFromSortKey(SortKey.SORT_PRIMARY_PRODUCT_YIELD),
          flexGrow: 0,
          isSortable: true,
          sortKey: SortKey.SORT_PRIMARY_PRODUCT_YIELD,
          width: 200,
        },
        render: (item) => {
          const products = (item.productYields ?? []).filter((p) => p.amountYield != null);
          if (products.length === 0) {
            return null;
          }
          return (
            <Stack spacing="xs">
              {products.map((p, i) => (
                <ProductYield key={i} name={p.name} amountYield={p.amountYield!} />
              ))}
            </Stack>
          );
        },
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PARENT_ENTRY,
          header: 'Parent Entry',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => (item.entry?.__typename === 'EntryNode' ? item.entry.name : null),
      },
      maybeStudiesColumn,
    ]);
  }

  getNonPluginRowContentHeight(item: ReactionGraphQL, extras: ReactionListingExtras): number {
    const baseHeight = item.scheme ? REACTION_SCHEME_HEIGHT : TABLE_ROW_BODY_HEIGHT;
    return Math.max(baseHeight, getStudiesRowContentHeight(item.id, extras.itemStudyAttributes));
  }

  openContextMenu(item: ReactionGraphQL, e: BasicEvent, _options: unknown): void {
    ReactionContextMenu.open(e.nativeEvent, item);
  }
}
